import React from 'react'
import PropTypes from 'prop-types'

import * as firebase from 'firebase'
import { connect } from 'react-redux'
import moment from 'moment'
import RaisedButton from 'material-ui/RaisedButton'

import JobStatus from 'infra/JobStatus'
import ClockingType from 'infra/ClockingType'
import EventCardIndicators from './EventCardIndicators'
import { startEventSync, stopEventSync } from 'infra/actions/events'
import ClockUtils from '../common/ClockUtils'

class JobCard extends React.PureComponent {

  static propTypes = {
    dateStr: PropTypes.string.isRequired,
    eventId: PropTypes.string.isRequired,
    eventInfo: PropTypes.object,
    userId: PropTypes.string.isRequired,
    userCalendarsList: PropTypes.array,
    dispatch: PropTypes.func.isRequired
  }

  constructor(props) {
    super(props)
    this.state = {
      showReminder: false,
      status: {}
    }
  }

  componentDidMount() {
    this.props.dispatch(startEventSync(this.props.eventId))
    this.attachDisplayPropsListener(this.props)
  }

  componentWillUnmount() {
    this.props.dispatch(stopEventSync(this.props.eventId))
    this.detachDisplayPropsListener(this.props)
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.dateStr !== this.props.dateStr ||
      nextProps.eventId !== this.props.eventId ||
      nextProps.userId !== this.props.userId) {

      this.props.dispatch(stopEventSync(this.props.eventId))
      this.props.dispatch(startEventSync(nextProps.eventId))

      this.detachDisplayPropsListener(this.props)
      this.attachDisplayPropsListener(nextProps)
    }
  }

  attachDisplayPropsListener = props => {
    const path = this.getDisplayPropertiesPath(props)
    if (path){
      firebase
        .database()
        .ref(path)
        .on('value', this.onDisplayPropertiesValue)
    }
  }

  detachDisplayPropsListener = props => {
    const path = this.getDisplayPropertiesPath(props)
    if (path){
      firebase
        .database()
        .ref(path)
        .off('value', this.onDisplayPropertiesValue)
    }
  }

  getDisplayPropertiesPath = (props) => {
    const { eventId, dateStr } = props
    if (eventId && dateStr){
      return `eventDisplays/${eventId}/${dateStr}`
    } else {
      return null
    }
  }

  onDisplayPropertiesValue = snapshot => {
    const displayProperties = snapshot && snapshot.val()

    const showReminder = Boolean(
      displayProperties
      && displayProperties.reminderSet
    )

    const status = displayProperties && displayProperties.status
      ? displayProperties.status
      : {}

    this.setState({ showReminder, status })
  }

  getUserCalendarColor = calendarId => {
    if (this.props.userCalendarsList) {
      const calendar = this.props.userCalendarsList.find(uc => uc.id === calendarId)
      if (calendar) {
        return calendar.backgroundColor
      }
    }
    return 'gray'
  }

  getJobStatus = modules => {
    const clockingType = ClockUtils.getClockingType(modules)
    if (clockingType === ClockingType.CLOCKING_WORKER || clockingType === ClockingType.CLOCKING_LINE_ITEMS) {
      const userStatus = this.state.status[this.props.userId]
      return JobStatus[userStatus]
    }
    return JobStatus[this.state.status]
  }

  getTarget = (modules) => {
    if (modules.clocking) {
      return modules.clocking.target
    }
    if (modules.clockingTeam) {
      return modules.clockingTeam.target
    }
    if (modules.clockingExtended) {
      return modules.clockingExtended.target
    }
    if (modules.clockingWorker) {
      return modules.clockingWorker.target
    }
    if (modules.clockingLineItems) {
      return modules.clockingLineItems.target
    }
    return null
  }

  getJobSummary = modules => {
    // If it has time. Show only the time
    if (modules.date.time && modules.date.time.enabled) {
      const startTime = moment(modules.date.time.startTime, 'HH:mm')
      const endTime = moment(modules.date.time.endTime, 'HH:mm')
      const startTimeStr = startTime.format('h:mm a')
      const endTimeStr = endTime.format('h:mm a')
      return `${startTimeStr} - ${endTimeStr}`
    }
    const target = this.getTarget(modules)
    if (target) {
      return `${target} mins`
    }
    return null
  }

  handleJobCardClicked = () => {
    this.props.dispatch({
      type: 'UI_DIALOG',
      label: 'JOB_SCREEN',
      value: {
        jobInfo: this.props.eventInfo,
        dateStr: this.props.dateStr
      }
    })
  }

  render() {
    if (!this.props.eventInfo) {
      return null
    }
    const modules = this.props.eventInfo.modules
    const summary = this.getJobSummary(modules)
    const jobStatus = this.getJobStatus(modules)
    const showSource = this.props.eventInfo.source && this.props.eventInfo.source.value === 'gcal'
    const sourceColor = showSource
      ? this.getUserCalendarColor(this.props.eventInfo.source.calendarId)
      : null
    return (
      <RaisedButton
        style={{ ...this.props.style, textAlign: 'left', lineHeight: 0 }}
        backgroundColor={jobStatus === JobStatus.COMPLETED ? '#527da3' : '#dbefff'}
        onTouchTap={this.handleJobCardClicked}>
        <div style={styles.container}>
          <div style={styles.cardTop}>
            <div style={styles.jobCardName(jobStatus)}>
              { this.props.eventInfo.modules.displayName.value }
            </div>
            <EventCardIndicators
              jobStatus={jobStatus}
              showReminder={this.state.showReminder}
              showSource={showSource}
              sourceColor={sourceColor} />
          </div>
          <div style={styles.cardBottom}>
            <div style={styles.subtitle(jobStatus)}>
              { modules.address.value }
            </div>
            <div style={{ height: 1 }} />
            <div style={styles.subtitle(jobStatus)}>
              { summary }
            </div>
          </div>
        </div>
      </RaisedButton>
    )
  }
}

const mapStateToProps = (state, ownProps) => ({
  userCalendarsList: state.userCalendars.list,
  eventInfo: state.firebase.calendarEvents[ownProps.eventId]
})

export default connect(mapStateToProps)(JobCard)

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: 64
  },
  cardTop: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontWeight: 400,
    textAlign: 'left',
    marginTop: 6,
    marginLeft: 4,
    marginRight: 4,
    marginBottom: 2,
    lineHeight: 1.2
  },
  cardBottom: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'left',
    marginBottom: 6,
    marginLeft: 4,
    marginRight: 4,
    fontSize: 11
  },
  subtitle: status => ({
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.25,
    fontFamily: 'Roboto, sans-serif',
    color: status === JobStatus.COMPLETED ? '#FFF' : 'rgba(0,0,0,0.4)'
  }),
  jobCardName: status => ({
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: 12,
    fontFamily: 'Roboto, sans-serif',
    color: status === JobStatus.COMPLETED ? '#FFF' : '#000'
  })
}



// WEBPACK FOOTER //
// ./src/schedule/common/JobCard.js